/**
 * Phase 8+1 — Advanced PDF filter dialog.
 *
 * Lets the user cherry-pick exactly which canonical sections and which
 * `lib_restaurants` venues end up in the rendered PDF. Submits by
 * calling `onExport({ sections, venues })` — the parent assembles the
 * download URL via `buildVoyagePdfUrl`.
 *
 * Behaviour:
 *   * All sections + all venues pre-checked on open.
 *   * Venue groups for `regular_restaurants` / `specialty_restaurants` /
 *     `buffets` only appear when the matching section is checked.
 *   * "Reset" link re-checks everything.
 *   * Export button is disabled when zero sections are checked.
 *
 * No new dependencies — uses the existing shadcn UI primitives
 * (`Dialog`, `Checkbox`, `Button`, `Separator`).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Download, RotateCcw } from "lucide-react";
import {
  PDF_FILTER_SECTION_KEYS,
  PDF_FILTER_SECTION_LABELS,
  PDF_TYPE_TO_SECTION,
} from "@/lib/settingsApi";

const VENUE_HOSTING_SECTIONS = new Set([
  "regular_restaurants",
  "specialty_restaurants",
  "buffets",
]);

export default function AdvancedPdfFilterDialog({
  open,
  onOpenChange,
  restaurants = [],   // [{id, name, type, enabled, ...}]
  onExport,           // ({sections: string[], venues: string[]}) => void
}) {
  // Section checkbox state (key → bool).
  const [sectionsChecked, setSectionsChecked] = useState(() => {
    const m = {};
    PDF_FILTER_SECTION_KEYS.forEach((k) => { m[k] = true; });
    return m;
  });
  // Venue checkbox state (restaurant_id → bool).
  const [venuesChecked, setVenuesChecked] = useState({});
  // Phase 14 — buffet layout radio (Kitchen / Line). Choice persists
  // across reloads via localStorage so the user doesn't have to re-pick
  // it every time they open the dialog.
  const [buffetLayout, setBuffetLayoutState] = useState(() => {
    try {
      const v = window.localStorage.getItem("vm:buffet_layout");
      return v === "line" ? "line" : "kitchen";
    } catch {
      return "kitchen";
    }
  });
  const setBuffetLayout = (next) => {
    setBuffetLayoutState(next);
    try {
      window.localStorage.setItem("vm:buffet_layout", next);
    } catch {
      // localStorage unavailable (private mode) — choice still
      // honoured for this session.
    }
  };

  // Group restaurants by section_key for rendering.
  const venuesBySection = useMemo(() => {
    const groups = { regular_restaurants: [], specialty_restaurants: [], buffets: [] };
    for (const r of restaurants || []) {
      if (r?.enabled === false) continue;
      const sec = PDF_TYPE_TO_SECTION[(r.type || "").toLowerCase()];
      if (sec && groups[sec]) groups[sec].push(r);
    }
    // Stable sort: sort_order then name.
    Object.values(groups).forEach((arr) => arr.sort((a, b) => {
      const sa = a.sort_order ?? 9999;
      const sb = b.sort_order ?? 9999;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    }));
    return groups;
  }, [restaurants]);

  // Reset on every open — start with everything checked.
  useEffect(() => {
    if (!open) return;
    const sMap = {};
    PDF_FILTER_SECTION_KEYS.forEach((k) => { sMap[k] = true; });
    setSectionsChecked(sMap);
    const vMap = {};
    Object.values(venuesBySection).flat().forEach((r) => { vMap[r.id] = true; });
    setVenuesChecked(vMap);
  }, [open, venuesBySection]);

  const anySectionChecked = useMemo(
    () => PDF_FILTER_SECTION_KEYS.some((k) => sectionsChecked[k]),
    [sectionsChecked],
  );

  const allSectionsChecked = useMemo(
    () => PDF_FILTER_SECTION_KEYS.every((k) => sectionsChecked[k]),
    [sectionsChecked],
  );
  const allVenuesChecked = useMemo(() => {
    const all = Object.values(venuesBySection).flat();
    if (all.length === 0) return true;
    return all.every((r) => venuesChecked[r.id]);
  }, [venuesBySection, venuesChecked]);
  const isFullExport = allSectionsChecked && allVenuesChecked;

  const handleReset = () => {
    const sMap = {};
    PDF_FILTER_SECTION_KEYS.forEach((k) => { sMap[k] = true; });
    setSectionsChecked(sMap);
    const vMap = {};
    Object.values(venuesBySection).flat().forEach((r) => { vMap[r.id] = true; });
    setVenuesChecked(vMap);
  };

  const toggleSection = (key) => {
    setSectionsChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleVenue = (id) => {
    setVenuesChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExport = () => {
    // Compute selected sections / venues.
    const selectedSections = PDF_FILTER_SECTION_KEYS.filter((k) => sectionsChecked[k]);
    // For per-venue sections that are checked, find which venues are checked.
    // Only emit a `venues=` filter if at least one section has a SUBSET of
    // its venues — otherwise everything is selected and the backend treats
    // `venues=...` as "all of these" which is the same as nothing.
    let selectedVenues = [];
    let venueSubset = false;
    for (const sec of ["regular_restaurants", "specialty_restaurants", "buffets"]) {
      if (!sectionsChecked[sec]) continue;
      const venuesInSec = venuesBySection[sec] || [];
      const checked = venuesInSec.filter((r) => venuesChecked[r.id]);
      if (checked.length < venuesInSec.length) venueSubset = true;
      checked.forEach((r) => selectedVenues.push(r.id));
    }

    const payload = {
      sections: isFullExport ? undefined : selectedSections,
      venues: (venueSubset ? selectedVenues : undefined),
      // Phase 14 — always send the toggle so the URL builder can decide
      // whether to attach `?buffet_layout=` to the export URL.
      buffet_layout: buffetLayout,
    };
    onExport(payload);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="pdf-filter-dialog"
      >
        <DialogHeader>
          <DialogTitle>Advanced PDF filter</DialogTitle>
          <DialogDescription>
            Choose which sections and which venues end up in the rendered PDF. The same
            voyage-day footer prints on every page regardless of the filter.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {/* Sections */}
          <section className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Sections
            </h3>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5">
              {PDF_FILTER_SECTION_KEYS.map((k) => (
                <label
                  key={k}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                  data-testid={`pdf-filter-section-${k}`}
                >
                  <Checkbox
                    checked={!!sectionsChecked[k]}
                    onCheckedChange={() => toggleSection(k)}
                    aria-label={PDF_FILTER_SECTION_LABELS[k]}
                  />
                  <span>{PDF_FILTER_SECTION_LABELS[k]}</span>
                </label>
              ))}
            </div>
          </section>

          <Separator className="my-3" />

          {/* Venues — one block per checked venue-hosting section */}
          {["regular_restaurants", "specialty_restaurants", "buffets"].map((sec) => {
            if (!sectionsChecked[sec]) return null;
            const venuesIn = venuesBySection[sec] || [];
            if (venuesIn.length === 0) return null;
            return (
              <section key={sec} className="mb-4" data-testid={`pdf-filter-venue-group-${sec}`}>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {PDF_FILTER_SECTION_LABELS[sec]} — venues
                </h3>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5">
                  {venuesIn.map((r) => (
                    <label
                      key={r.id}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                      data-testid={`pdf-filter-venue-${r.id}`}
                    >
                      <Checkbox
                        checked={!!venuesChecked[r.id]}
                        onCheckedChange={() => toggleVenue(r.id)}
                        aria-label={r.name}
                      />
                      <span>{r.name}</span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Phase 14 — Buffet layout toggle. Radio with localStorage
            persistence; the selection is honored on every Export. */}
        <Separator />
        <div className="space-y-2"
             data-testid="pdf-filter-buffet-layout-group">
          <p className="text-sm font-semibold">Buffet layout</p>
          <p className="text-xs text-muted-foreground">
            How OVC + crew mess buffet stations are grouped on the PDF.
            Per-day buffet handouts and Excel exports honour the same
            choice (saved to your browser).
          </p>
          <div className="flex flex-col gap-1">
            {[
              { val: "kitchen", label: "Kitchen (Hot / Cold / Pastry)" },
              { val: "line",    label: "Line / Island" },
            ].map((opt) => (
              <label
                key={opt.val}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-2 py-1"
                data-testid={`pdf-filter-buffet-layout-${opt.val}`}
              >
                <input
                  type="radio"
                  name="pdf-filter-buffet-layout"
                  value={opt.val}
                  checked={buffetLayout === opt.val}
                  onChange={() => setBuffetLayout(opt.val)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter className="items-center sm:items-center sm:justify-between sm:flex-row gap-3 flex-row">
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            onClick={handleReset}
            data-testid="pdf-filter-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              data-testid="pdf-filter-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              disabled={!anySectionChecked}
              title={anySectionChecked ? "" : "Select at least one section"}
              data-testid="pdf-filter-export"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
